import Phaser from 'phaser';
import { GameState } from '@/state/GameState';
import { hasCutscene } from '@/data/cutscene';
import { nextStageRoute, shouldShowDeal, dealIntroRoute } from '@/systems/stageFlow';
import { MusicDirectorAdapter } from '@/systems/MusicDirectorAdapter';
import { createPhaserMusicFactory } from '@/systems/createPhaserMusicFactory';
import { getSaveManager, ACTIVE_SLOT } from '@/systems/SaveManager';
import { isSurvivalNewRecord } from '@/systems/SurvivalScoring';
import { BOSS_RUSH_SEQUENCE } from '@/systems/BossRushSequence';

/**
 * ResultsScene — shown after a stage ends (cleared or game over).
 *
 * Branches on GameState:
 *   - Game over        → "GAME OVER", any key returns to Title.
 *   - Final stage clear → "LONDON IS YOURS", any key returns to Title.
 *   - Mid-run clear     → "STAGE CLEAR", any key continues to next Stage.
 */
export class ResultsScene extends Phaser.Scene {
  private music!: MusicDirectorAdapter;

  constructor() {
    super({ key: 'ResultsScene' });
  }

  create(): void {
    const { width, height } = this.scale;
    const run = GameState.current;

    const gameOver = GameState.isGameOver;
    const finished = GameState.isFinalStageCleared;

    // Survival: record the run iff it beats the slot's stored best, capturing
    // whether THIS run set a new record (for the gloat banner) before writing.
    const survival = run.gameMode === 'survival';
    const bossRush = run.gameMode === 'boss-rush';
    const scoreAttack = run.gameMode === 'score-attack';
    let recordLine = '';
    if (survival) {
      const sm = getSaveManager();
      const prev = sm.load(ACTIVE_SLOT)?.survivalRecord ?? null;
      const isNew = isSurvivalNewRecord(run.score, prev);
      sm.recordSurvival(
        ACTIVE_SLOT,
        { score: run.score, wave: run.survivalWave, heroId: run.heroId ?? 'ray', date: Date.now() },
        Date.now(),
      );
      recordLine = isNew ? '\n★ NEW RECORD ★' : `\nBEST  ${prev?.score ?? run.score}`;
    }

    this.music = new MusicDirectorAdapter(undefined, createPhaserMusicFactory(this));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.music.stopAll());
    if (gameOver) {
      this.music.onSceneChange('gameOver');
    } else if (finished) {
      this.music.onSceneChange('victory');
    }

    let heading = 'STAGE CLEAR';
    let colour = '#06d6a0';
    if (gameOver) {
      heading = 'GAME OVER';
      colour = '#ef233c';
    } else if (finished) {
      heading = 'LONDON IS YOURS';
      colour = '#ffba08';
    }
    if (survival) {
      heading = 'SURVIVAL OVER';
      colour = '#ffba08';
    }
    if (bossRush) {
      heading = gameOver ? 'GAME OVER' : 'BOSS RUSH CLEAR';
      colour = gameOver ? '#ef233c' : '#ffba08';
    }
    if (scoreAttack) {
      heading = gameOver ? 'GAME OVER' : 'SCORE ATTACK COMPLETE';
      colour = gameOver ? '#ef233c' : '#ffba08';
    }

    this.add
      .text(width / 2, height / 3, heading, {
        fontFamily: 'Impact, "Arial Black", sans-serif',
        fontSize: '72px',
        color: colour,
        stroke: '#0b0e14',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    this.add
      .text(
        width / 2,
        height / 2,
        bossRush
          ? `BOSSES  ${run.bossRushIndex}/${BOSS_RUSH_SEQUENCE.length}\nSCORE  ${run.score}`
          : scoreAttack
            ? `SCORE  ${run.score}`
            : survival
              ? `WAVE  ${run.survivalWave}\nSCORE  ${run.score}${recordLine}`
              : `SCORE  ${run.score}\nLIVES  ${run.lives}`,
        {
          fontFamily: '"Courier New", monospace',
          fontSize: '24px',
          color: '#ffffff',
          align: 'center',
        },
      )
      .setOrigin(0.5);

    const continues = !gameOver && !finished && !bossRush && !scoreAttack;
    const prompt = this.add
      .text(
        width / 2,
        (height * 2) / 3,
        continues ? 'PRESS ANY KEY — NEXT STAGE' : 'PRESS ANY KEY — TITLE',
        {
          fontFamily: '"Courier New", monospace',
          fontSize: '20px',
          color: '#ffffff',
        },
      )
      .setOrigin(0.5);

    this.tweens.add({
      targets: prompt,
      alpha: { from: 1, to: 0.2 },
      duration: 700,
      yoyo: true,
      repeat: -1,
    });

    this.input.keyboard?.once('keydown', () => {
      if (continues) {
        const nextStage = GameState.current.stage;
        if (shouldShowDeal(nextStage)) {
          const dealRoute = dealIntroRoute(hasCutscene('halloway-deal'));
          this.scene.start(dealRoute.sceneKey, dealRoute.data);
        } else {
          const route = nextStageRoute(nextStage, hasCutscene(`stage${nextStage}-intro`));
          this.scene.start(route.sceneKey, route.data);
        }
      } else if (finished && !scoreAttack) {
        this.scene.start('CreditsScene');
      } else {
        this.scene.start('TitleScene');
      }
    });
  }

  update(_time: number, delta: number): void {
    this.music.update(delta);
  }

  shutdown(): void {
    this.music.stopAll();
  }
}
